import { useState, useEffect, useRef, useCallback } from "react";
import {
  Upload, Search, Trash2, BookOpen, Loader2, Plus, X,
  Edit3, CheckCircle2, Zap, HardHat, Package, Save
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { opts, fmt } from "./constants";

// ─── MATERIALES TAB (existing Excel price lists) ─────────────────────────────
function MaterialesTab() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [proveedor, setProveedor] = useState("");
  const [viewItems, setViewItems] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const fileRef = useRef(null);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/budgets/price-lists/all`, opts);
      setLists(res.data || []);
    } catch { toast.error("Error al cargar listas"); }
    finally { setLoading(false); }
  };

  const fetchItems = async (prov) => {
    try {
      const res = await axios.get(`${API}/budgets/price-lists/${encodeURIComponent(prov)}/items`, opts);
      setViewItems({ proveedor: prov, items: res.data.items || [] });
    } catch { toast.error("Error al cargar ítems"); }
  };

  useEffect(() => { fetchLists(); }, []);

  const handleImport = async () => {
    if (!proveedor.trim()) { toast.error("Ingresa el nombre del proveedor"); return; }
    const file = fileRef.current?.files[0];
    if (!file) { toast.error("Selecciona un archivo Excel"); return; }
    const fd = new FormData();
    fd.append("proveedor", proveedor);
    fd.append("file", file);
    setImporting(true);
    try {
      const res = await axios.post(`${API}/budgets/price-lists/import`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message);
      setProveedor("");
      if (fileRef.current) fileRef.current.value = "";
      fetchLists();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar"); }
    finally { setImporting(false); }
  };

  const handleDelete = async (prov) => {
    if (!window.confirm(`¿Eliminar lista de ${prov}?`)) return;
    try {
      await axios.delete(`${API}/budgets/price-lists/${encodeURIComponent(prov)}`, opts);
      toast.success("Lista eliminada");
      fetchLists();
    } catch { toast.error("Error al eliminar"); }
  };

  if (viewItems) {
    const filtered = searchQ.length > 1
      ? viewItems.items.filter(i => i.descripcion?.toLowerCase().includes(searchQ.toLowerCase()))
      : viewItems.items;
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setViewItems(null)} className="text-[#2E6DA4] text-sm font-semibold hover:underline">← Volver</button>
          <h4 className="font-bold text-gray-900">{viewItems.proveedor}</h4>
          <span className="text-xs text-gray-400">{viewItems.items.length} ítems</span>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Buscar en lista..." className="w-full border border-gray-200 rounded-xl pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30" />
        </div>
        <div className="divide-y divide-gray-50 max-h-[50vh] overflow-y-auto rounded-xl border border-gray-100">
          {filtered.map((item, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
              <div>
                <p className="text-xs font-medium text-gray-800">{item.descripcion}</p>
                <p className="text-xs text-gray-400">{item.unidad}</p>
              </div>
              <span className="text-xs font-bold text-[#2E6DA4]">{fmt(item.precio_iva_inc)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="bg-[#EBF3FB] border border-[#C5DCF0] rounded-2xl p-5">
        <h4 className="font-bold text-[#1A4B7A] mb-3 flex items-center gap-2"><Upload size={15} /> Importar Lista de Precios (Excel)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input value={proveedor} onChange={e => setProveedor(e.target.value)}
            placeholder="Nombre del proveedor (ej: BGA, NEXANS)" className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30" data-testid="provider-input" />
          <input type="file" accept=".xlsx,.xls" ref={fileRef}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm file:mr-3 file:text-xs file:font-semibold file:bg-[#2E6DA4] file:text-white file:border-0 file:rounded-lg file:px-2 file:py-1" data-testid="file-input" />
          <button onClick={handleImport} disabled={importing}
            className="flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2 text-sm font-semibold transition-colors disabled:opacity-60" data-testid="import-btn">
            {importing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {importing ? "Importando..." : "Importar"}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Columna 1 = Descripción, resto = Unidad / Precio (valor &gt; 100 PYG)</p>
      </div>

      {loading ? <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14 rounded-xl" />)}</div> :
        lists.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <BookOpen size={40} className="mx-auto mb-3" />
            <p className="font-medium">No hay listas importadas</p>
          </div>
        ) : (
          <div className="space-y-2">
            {lists.map(pl => (
              <div key={pl._id} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-5 py-3 shadow-sm hover:shadow-md transition-all">
                <div>
                  <p className="font-bold text-gray-900">{pl.proveedor}</p>
                  <p className="text-xs text-gray-400">{pl.item_count || "—"} ítems · Actualizado: {pl.updated_at?.slice(0, 10) || "—"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => fetchItems(pl.proveedor)} className="px-3 py-1.5 text-xs font-semibold text-[#2E6DA4] hover:bg-[#EBF3FB] rounded-lg transition-colors">Ver ítems</button>
                  <button onClick={() => handleDelete(pl.proveedor)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ─── BOCAS CATALOG ─────────────────────────────────────────────────────────
const BOCA_EMPTY = { tipo_boca: "", descripcion: "", precio_mat: 0, precio_mo: 0, unidad: "un" };

function BocasTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [editItem, setEditItem] = useState(null); // null = closed, object = new/edit
  const [saving, setSaving] = useState(false);
  const [importData, setImportData] = useState(""); // CSV paste
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bocas-precios/bocas`, opts);
      setItems(res.data || []);
    } catch { toast.error("Error al cargar bocas"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSave = async () => {
    if (!editItem.tipo_boca) { toast.error("Ingresa el tipo de boca"); return; }
    setSaving(true);
    try {
      if (editItem._id) {
        await axios.put(`${API}/bocas-precios/bocas/${editItem._id}`, editItem, opts);
        toast.success("Boca actualizada");
      } else {
        await axios.post(`${API}/bocas-precios/bocas`, editItem, opts);
        toast.success("Boca agregada");
      }
      setEditItem(null);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    try {
      await axios.delete(`${API}/bocas-precios/bocas/${id}`, opts);
      toast.success("Boca eliminada");
      fetchItems();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleBulkImport = async () => {
    if (!importData.trim()) return;
    const lines = importData.trim().split("\n").filter(l => l.trim());
    const parsed = [];
    for (const line of lines) {
      const cols = line.split(/[\t,;]/).map(c => c.trim().replace(/"/g, ""));
      if (cols.length < 2) continue;
      const mat = parseFloat(cols[2]) || 0;
      const mo = parseFloat(cols[3]) || 0;
      parsed.push({
        tipo_boca: cols[0] || "",
        descripcion: cols[1] || cols[0] || "",
        precio_mat: mat,
        precio_mo: mo,
        unidad: cols[4] || "un",
      });
    }
    if (parsed.length === 0) { toast.error("No se encontraron datos válidos"); return; }
    setImporting(true);
    try {
      await axios.post(`${API}/bocas-precios/bocas/bulk`, parsed, opts);
      toast.success(`${parsed.length} bocas importadas`);
      setImportData("");
      setShowImport(false);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar"); }
    finally { setImporting(false); }
  };

  const filtered = searchQ.length > 1
    ? items.filter(i => i.tipo_boca?.toLowerCase().includes(searchQ.toLowerCase()) || i.descripcion?.toLowerCase().includes(searchQ.toLowerCase()))
    : items;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Buscar boca..." className="w-full border border-gray-200 rounded-xl pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30" />
        </div>
        <button onClick={() => setShowImport(s => !s)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#2E6DA4] border border-[#2E6DA4]/30 rounded-xl hover:bg-[#EBF3FB] transition-colors">
          <Upload size={13} /> Importar
        </button>
        <button onClick={() => setEditItem({ ...BOCA_EMPTY })} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#2E6DA4] text-white rounded-xl hover:bg-[#245A8A] transition-colors" data-testid="add-boca-btn">
          <Plus size={13} /> Nueva Boca
        </button>
      </div>

      {showImport && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
          <p className="text-xs font-bold text-amber-700">Importar bocas desde Excel (pegar datos)</p>
          <p className="text-xs text-amber-600">Formato: <code>Tipo_Boca | Descripción | Precio_Mat | Precio_MO | Unidad</code> (separado por tab, coma o punto y coma)</p>
          <textarea
            value={importData}
            onChange={e => setImportData(e.target.value)}
            rows={6}
            placeholder={"Boca de Iluminación\tBoca de iluminación simple\t85000\t50000\tun\nTomacorriente\tTomacorriente doble\t120000\t60000\tun"}
            className="w-full border border-amber-200 rounded-xl px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-amber-400/30 resize-none bg-white"
            data-testid="bocas-import-textarea"
          />
          <div className="flex gap-2">
            <button onClick={() => { setShowImport(false); setImportData(""); }} className="px-3 py-1.5 text-xs border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button onClick={handleBulkImport} disabled={importing} className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-60" data-testid="bocas-import-confirm-btn">
              {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {importing ? "Importando..." : "Importar datos"}
            </button>
          </div>
        </div>
      )}

      {/* Inline edit form */}
      {editItem && (
        <div className="bg-[#EBF3FB] border border-[#C5DCF0] rounded-2xl p-4 space-y-3">
          <p className="text-xs font-bold text-[#1A4B7A]">{editItem._id ? "Editar Boca" : "Nueva Boca"}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Boca *</label>
              <input value={editItem.tipo_boca} onChange={e => setEditItem(s => ({ ...s, tipo_boca: e.target.value }))}
                placeholder="Ej: Boca de Iluminación" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="boca-tipo-input" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
              <input value={editItem.descripcion} onChange={e => setEditItem(s => ({ ...s, descripcion: e.target.value }))}
                placeholder="Descripción detallada" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Precio Mat. (PYG)</label>
              <input type="number" value={editItem.precio_mat} onChange={e => setEditItem(s => ({ ...s, precio_mat: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="boca-precio-mat-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Precio M.O. (PYG)</label>
              <input type="number" value={editItem.precio_mo} onChange={e => setEditItem(s => ({ ...s, precio_mo: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="boca-precio-mo-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unidad</label>
              <input value={editItem.unidad} onChange={e => setEditItem(s => ({ ...s, unidad: e.target.value }))}
                placeholder="un" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditItem(null)} className="flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50"><X size={12} /> Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 px-4 py-2 text-xs font-semibold bg-[#2E6DA4] text-white rounded-xl hover:bg-[#245A8A] disabled:opacity-60" data-testid="boca-save-btn">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 rounded-xl" />)}</div> :
        filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Zap size={40} className="mx-auto mb-3 text-gray-200" />
            <p className="font-medium text-gray-500">No hay bocas cargadas</p>
            <p className="text-xs mt-1">Agrega bocas manualmente o impórtalas desde Excel</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">Tipo de Boca</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Descripción</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">P. Mat.</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">P. M.O.</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">Ud.</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(item => (
                  <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{item.tipo_boca}</td>
                    {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                    <td className="px-4 py-2.5 text-gray-500 hidden sm:table-cell max-w-[280px] whitespace-normal break-words" title={item.descripcion}>{item.descripcion}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#2E6DA4]">{fmt(item.precio_mat)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-emerald-600">{fmt(item.precio_mo)}</td>
                    <td className="px-4 py-2.5 text-center text-gray-400">{item.unidad}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setEditItem({ ...item })} className="p-1.5 text-gray-400 hover:text-[#2E6DA4] hover:bg-[#EBF3FB] rounded-lg transition-colors"><Edit3 size={13} /></button>
                        <button onClick={() => handleDelete(item._id, item.tipo_boca)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

// ─── MANO DE OBRA CATALOG ───────────────────────────────────────────────────
const MO_EMPTY = { categoria: "", descripcion: "", precio_unitario: 0, unidad: "hs" };

function ManoObraTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importData, setImportData] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bocas-precios/mano-obra`, opts);
      setItems(res.data || []);
    } catch { toast.error("Error al cargar mano de obra"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSave = async () => {
    if (!editItem.categoria) { toast.error("Ingresa la categoría"); return; }
    setSaving(true);
    try {
      if (editItem._id) {
        await axios.put(`${API}/bocas-precios/mano-obra/${editItem._id}`, editItem, opts);
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/bocas-precios/mano-obra`, editItem, opts);
        toast.success("Categoría agregada");
      }
      setEditItem(null);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    try {
      await axios.delete(`${API}/bocas-precios/mano-obra/${id}`, opts);
      toast.success("Categoría eliminada");
      fetchItems();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleBulkImport = async () => {
    if (!importData.trim()) return;
    const lines = importData.trim().split("\n").filter(l => l.trim());
    const parsed = [];
    for (const line of lines) {
      const cols = line.split(/[\t,;]/).map(c => c.trim().replace(/"/g, ""));
      if (cols.length < 2) continue;
      parsed.push({
        categoria: cols[0] || "",
        descripcion: cols[1] || cols[0] || "",
        precio_unitario: parseFloat(cols[2]) || 0,
        unidad: cols[3] || "hs",
      });
    }
    if (parsed.length === 0) { toast.error("No se encontraron datos válidos"); return; }
    setImporting(true);
    try {
      await axios.post(`${API}/bocas-precios/mano-obra/bulk`, parsed, opts);
      toast.success(`${parsed.length} categorías importadas`);
      setImportData("");
      setShowImport(false);
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar"); }
    finally { setImporting(false); }
  };

  const filtered = searchQ.length > 1
    ? items.filter(i => i.categoria?.toLowerCase().includes(searchQ.toLowerCase()) || i.descripcion?.toLowerCase().includes(searchQ.toLowerCase()))
    : items;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Buscar categoría..." className="w-full border border-gray-200 rounded-xl pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30" />
        </div>
        <button onClick={() => setShowImport(s => !s)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#2E6DA4] border border-[#2E6DA4]/30 rounded-xl hover:bg-[#EBF3FB] transition-colors">
          <Upload size={13} /> Importar
        </button>
        <button onClick={() => setEditItem({ ...MO_EMPTY })} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#2E6DA4] text-white rounded-xl hover:bg-[#245A8A] transition-colors" data-testid="add-mo-btn">
          <Plus size={13} /> Nueva Categoría
        </button>
      </div>

      {showImport && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-3">
          <p className="text-xs font-bold text-emerald-700">Importar mano de obra (pegar datos de Excel)</p>
          <p className="text-xs text-emerald-600">Formato: <code>Categoría | Descripción | Precio_Unitario | Unidad</code></p>
          <textarea
            value={importData}
            onChange={e => setImportData(e.target.value)}
            rows={5}
            placeholder={"Electricista\tElectricista oficial\t150000\ths\nAyudante\tAyudante de electricista\t80000\ths"}
            className="w-full border border-emerald-200 rounded-xl px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-emerald-400/30 resize-none bg-white"
            data-testid="mo-import-textarea"
          />
          <div className="flex gap-2">
            <button onClick={() => { setShowImport(false); setImportData(""); }} className="px-3 py-1.5 text-xs border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button onClick={handleBulkImport} disabled={importing} className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-60" data-testid="mo-import-confirm-btn">
              {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {importing ? "Importando..." : "Importar datos"}
            </button>
          </div>
        </div>
      )}

      {editItem && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-3">
          <p className="text-xs font-bold text-emerald-700">{editItem._id ? "Editar Categoría" : "Nueva Categoría de M.O."}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Categoría *</label>
              <input value={editItem.categoria} onChange={e => setEditItem(s => ({ ...s, categoria: e.target.value }))}
                placeholder="Ej: Electricista" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="mo-categoria-input" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
              <input value={editItem.descripcion} onChange={e => setEditItem(s => ({ ...s, descripcion: e.target.value }))}
                placeholder="Descripción del rol" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Precio Unitario (PYG)</label>
              <input type="number" value={editItem.precio_unitario} onChange={e => setEditItem(s => ({ ...s, precio_unitario: parseFloat(e.target.value) || 0 }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="mo-precio-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unidad</label>
              <select value={editItem.unidad} onChange={e => setEditItem(s => ({ ...s, unidad: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white">
                <option value="hs">hs (hora)</option>
                <option value="dia">día</option>
                <option value="jornal">jornal</option>
                <option value="mes">mes</option>
                <option value="gl">gl (global)</option>
                <option value="un">un (unidad)</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditItem(null)} className="flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50"><X size={12} /> Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 px-4 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-60" data-testid="mo-save-btn">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 rounded-xl" />)}</div> :
        filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <HardHat size={40} className="mx-auto mb-3 text-gray-200" />
            <p className="font-medium text-gray-500">No hay categorías de mano de obra</p>
            <p className="text-xs mt-1">Agrega categorías manualmente o importa desde Excel</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">Categoría</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Descripción</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">Precio Unit.</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-gray-500 uppercase tracking-wide">Ud.</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(item => (
                  <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{item.categoria}</td>
                    {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                    <td className="px-4 py-2.5 text-gray-500 hidden sm:table-cell max-w-[280px] whitespace-normal break-words" title={item.descripcion}>{item.descripcion}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-emerald-600">{fmt(item.precio_unitario)}</td>
                    <td className="px-4 py-2.5 text-center text-gray-400">{item.unidad}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setEditItem({ ...item })} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"><Edit3 size={13} /></button>
                        <button onClick={() => handleDelete(item._id, item.categoria)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

// ─── MAIN EXPORT: Tabbed Price Manager ────────────────────────────────────────
export function PriceListManager() {
  const [activeTab, setActiveTab] = useState("materiales");

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center gap-1 px-6 pt-2 border-b border-gray-100 bg-gray-50/50">
        {[
          { key: "materiales", label: "Materiales", icon: Package },
          { key: "bocas", label: "Bocas Eléctricas", icon: Zap },
          { key: "mano_obra", label: "Mano de Obra", icon: HardHat },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors -mb-px ${
                activeTab === tab.key
                  ? "border-[#2E6DA4] text-[#2E6DA4]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`price-tab-${tab.key}`}
            >
              <Icon size={13} /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "materiales" && <MaterialesTab />}
      {activeTab === "bocas" && <BocasTab />}
      {activeTab === "mano_obra" && <ManoObraTab />}
    </div>
  );
}
